import { useContext, useEffect, useState } from "react";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { EntityDetailProps, EntityDetailTableProps } from "../../../common/entity/EntityInterfaces";
import { RenderArray } from "../../../common/misc/EntityRenders/EntityRenderer";
import { NotSet } from "../../../common/misc/UIconstants";
import Detail from "../../../common/panels/Detail/Detail";
import { Table } from "../../../common/panels/Detail/DetailTable";
import { DetailsPageTopControlsView } from "../../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { DownloadZONTALDataset } from "../../ZontalCommons";
import { Container } from "../../../common/panels/Container/Container";
import { ZontalInformationPackageProfile, zontalIPPConstants } from "../../../api/Zontal";
import { useGet } from "../../../api/BaseEntityApi";
import { FilesStructure } from "../../../api/Datasets";
import {
  accumulateFileSizes,
  buildTree,
  FilesTreeElement,
  getTreeData,
  TreeElements,
} from "../../../Dataset/tiles/Files/Files";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { FileTree } from "../../../Dataset/common/Tree/Tree";

interface ZontalIPPFilesProps {
  dataset: ZontalInformationPackageProfile;
}
const ZontalIPPFiles = ({ dataset }: ZontalIPPFilesProps) => {
  const [struct, setStruct] = useState<TreeElements<FilesTreeElement>>([]);
  const {
    data: filesStructure,
    status,
    fetchStatus,
    error,
  } = useGet<FilesStructure>(`${zontalIPPConstants.resource}/${dataset.id}/files`, null, {
    enabled: !!dataset.id,
    cacheTime: 0,
  });

  useEffect(() => {
    if (filesStructure) {
      let _struct: TreeElements<FilesTreeElement> = [];
      accumulateFileSizes(filesStructure);
      buildTree(filesStructure, undefined, _struct);
      setStruct(_struct);
    }
  }, [filesStructure]);

  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
      {!!filesStructure && Array.isArray(struct) && !!struct.length && (
        <FileTree<FilesTreeElement> files={getTreeData<FilesTreeElement>(struct)} level={0} />
      )}
    </LoadingWrapper>
  );
};

export const ZontalIPPDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"zontalIPP">) => {
  const { api } = useContext(SessionContext);
  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity as any}
          enteredByFullOverride={entity.enteredBy.name}
          modifiedByFullOverride={entity.lastModifiedBy.name}
          entityConstants={entityConstants}
          controls={<DownloadZONTALDataset api={api} id={entity.id} />}
          hideHistoryModal
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <div className="flex col-nowrap" style={{ width: "100%", height: "fit-content", marginBottom: 20 }}>
          <ZontalIPPDetailTable
            entity={entity}
            entityConstants={entityConstants}
            fieldLabels={fieldLabels}
            entityTypeId={entityTypeId}
          />
          <Container title="Files" style={{ width: "100%", minHeight: "fit-content" }}>
            <ZontalIPPFiles dataset={entity} />
          </Container>
        </div>
      }
      sidebarPosition="hidden"
    />
  );
};

export const ZontalIPPDetailTable = ({ entity, entityConstants, fieldLabels }: EntityDetailTableProps<"zontalIPP">) => {
  return (
    <>
      <Table noPadding nonFoldable>
        <Table.Head>Details</Table.Head>
        <Table.Body>
          <Table.Body.RowContent title={fieldLabels.name} content={entity.name} />

          <Table.Body.RowContent
            title={fieldLabels.owners}
            content={
              <>
                <RenderArray values={entity.owners} style={{}}>
                  {(value, index) => <div className="flex row-nowrap gap-5">{value.name ?? value.id ?? NotSet}</div>}
                </RenderArray>
              </>
            }
          />

          <Table.Body.RowContent title={fieldLabels.version} content={entity.version ?? NotSet} />
          <Table.Body.RowContent title={fieldLabels.adfIdentifier} content={entity.adfIdentifier ?? NotSet} />
          <Table.Body.RowContent
            title={fieldLabels.adfVersion}
            content={<RenderArray values={entity.adfVersion}>{(value) => <span>{value}</span>}</RenderArray>}
          />
          <Table.Body.RowContent
            title={fieldLabels.digitalObjectsContentLength}
            content={entity.digitalObjectsContentLength ?? NotSet}
          />
          <Table.Body.RowContent
            title={fieldLabels.digitalObjectsCount}
            content={entity.digitalObjectsCount ?? NotSet}
          />
          <Table.Body.RowContent title={fieldLabels.contentIdentifier} content={entity.contentIdentifier ?? NotSet} />
          <Table.Body.RowContent
            title={fieldLabels.informationPackageProfile}
            content={entity.informationPackageProfile?.name ?? entity.informationPackageProfile?.id ?? NotSet}
          />
          <Table.Body.RowContent
            title={fieldLabels.lifecycleStatus}
            content={entity.lifecycleStatus?.name ?? entity.lifecycleStatus?.id ?? NotSet}
          />
          <Table.Body.RowContent
            title={fieldLabels.transitionStatus}
            content={entity.transitionStatus?.name ?? entity.transitionStatus?.id ?? NotSet}
          />
          <Table.Body.RowContent title={fieldLabels.datahubLoaded} content={entity.datahubLoaded ? "Yes" : "No"} />
          <Table.Body.RowContent
            title={fieldLabels.submittedBy}
            content={entity.submittedBy?.name ?? entity.submittedBy?.id ?? NotSet}
          />
          <Table.Body.RowContent
            title={fieldLabels.submittedOn}
            content={entity.submittedOn ? <DateTimeRenderer date={entity.submittedOn} /> : NotSet}
          />

          <Table.Body.RowContent title={fieldLabels.motivatedBy} content={entity.motivatedBy ?? NotSet} />
          <Table.Body.RowContent title={fieldLabels.expired} content={entity.expired ? "Yes" : "No"} />
          <Table.Body.RowContent title={fieldLabels.digestValue} content={entity.digestValue ?? NotSet} />
          <Table.Body.RowContent
            title={fieldLabels.owners}
            content={
              <RenderArray values={entity.owners} style={{}}>
                {(value, index) => <div className="flex row-nowrap gap-5">{value.name ?? value.id ?? NotSet}</div>}
              </RenderArray>
            }
          />
        </Table.Body>
      </Table>
    </>
  );
};
